import React from 'react'
import EditText from 'blocks.simple/editText/editText'
import Icon from 'blocks.simple/icon/icon'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { color } from 'theme'
import { useTemplateEditorContext } from '../hooks/templateEditorContext'
import { LEFT_PANE_TEMPLATES_MIN_HEIGHT } from '../hooks/useTemplateEditorLeftPaneState'
import { TemplateEditorLeftPane } from './templateEditor__leftPane'
import paneStyles from './templateEditor__leftPane.jcss'
import styles from './templateEditor__leftPanels.jcss'

export const TemplateEditorTemplatesPane = () => {
    const {
        collapsedPanes,
        paneHeights,
        togglePane,
        templates,
        templatesSearch,
        setTemplatesSearch,
        templateId,
        selectTemplate,
        loadMoreTemplates,
        hasMoreTemplates,
        isListLoading,
        isTemplateLoading,
        createNewTemplate,
        requestDeleteTemplate,
    } = useTemplateEditorContext()

    const collapsed = collapsedPanes.templates
    const height = Math.max(paneHeights.templates, LEFT_PANE_TEMPLATES_MIN_HEIGHT)

    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const target = event.currentTarget
        const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 48

        if (nearBottom) {
            loadMoreTemplates()
        }
    }

    return (
        <TemplateEditorLeftPane
            paneId="templates"
            title={translate('templateEditorMyTemplates')}
            collapsed={collapsed}
            onToggle={() => togglePane('templates')}
            height={height}
            headerAction={
                <button
                    type="button"
                    className={paneStyles.headerActionButton}
                    title={translate('templateEditorNew')}
                    aria-label={translate('templateEditorNew')}
                    onClick={() => createNewTemplate()}
                >
                    <Icon type="plus" size={14} />
                </button>
            }
        >
            <div className={styles.templatesTools}>
                <div className={styles.templatesSearch}>
                    <Icon type="search" size={14} />
                    <EditText
                        className={styles.templatesSearchInput}
                        placeholder={translate('templateEditorSearchTemplates')}
                        value={templatesSearch}
                        onChange={setTemplatesSearch}
                        rounded="none"
                    />
                </div>
            </div>

            {templates.length > 0 ? (
                <div className={styles.templatesGrid} onScroll={onScroll}>
                    {templates.map((template) => {
                        const isActive = template.id === templateId

                        return (
                            <div
                                key={template.id}
                                className={cn(styles.templateCard, isActive && styles.templateCardActive)}
                            >
                                <button
                                    type="button"
                                    data-template-delete=""
                                    className={styles.templateCardDelete}
                                    title={translate('templateEditorDeleteTemplate')}
                                    aria-label={translate('templateEditorDeleteTemplate')}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        requestDeleteTemplate(template.id, template.name)
                                    }}
                                >
                                    <Icon type="delete" size={12} color={color.critical} />
                                </button>
                                <button
                                    type="button"
                                    className={styles.templateCardBody}
                                    onClick={() => selectTemplate(template.id)}
                                    disabled={isTemplateLoading}
                                    title={template.name}
                                >
                                    <div className={styles.templateCardThumb}>
                                        {template.thumbnailUrl ? (
                                            <img
                                                src={template.thumbnailUrl}
                                                alt=""
                                                className={styles.templateCardImage}
                                            />
                                        ) : (
                                            <div className={styles.templateCardPlaceholder}>
                                                {template.canvasW}×{template.canvasH}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.templateCardName}>{template.name}</div>
                                </button>
                            </div>
                        )
                    })}
                    {isListLoading && <div className={styles.templatesEmpty}>{translate('loading')}</div>}
                    {!isListLoading && hasMoreTemplates && (
                        <button type="button" className={styles.templatesLoadMore} onClick={loadMoreTemplates}>
                            {translate('more')}
                        </button>
                    )}
                </div>
            ) : (
                <div className={styles.templatesEmpty}>
                    {isListLoading ? (
                        translate('loading')
                    ) : (
                        <>
                            <div>{translate('templateEditorNoTemplates')}</div>
                            <div className={styles.templatesEmptyHint}>{translate('templateEditorSaveLayoutHint')}</div>
                        </>
                    )}
                </div>
            )}
        </TemplateEditorLeftPane>
    )
}
